import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, Chip, TextField, FormControl, InputLabel, Select, MenuItem
} from '@mui/material'; // Import necessary Material-UI components
import { format } from 'date-fns';
import { Task, TaskStatus } from '../types/task';
import { taskService } from '../services/taskService';
import { useDebounce } from '../hooks/useDebounce';
import { useAuth } from '../hooks/UserContext';
import MyTaskDetailsModal from '../components/MyTaskDetailsModal';
import { Project } from '../types/project';
import { projectService } from '../services/projectService';

const statusColors: Record<TaskStatus, 'default' | 'info' | 'success'> = {
  TODO: 'default',
  IN_PROGRESS: 'info',
  DONE: 'success',
};

const MyTasksPage: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const fetchTasks = async (params: Parameters<typeof taskService.getTasks>[0]) => {
    try {
      const data = await taskService.getTasks(params);
      setTasks(data);
    } catch (err) {
      setError('Failed to fetch tasks.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch data for filter dropdowns
    const fetchFilterData = async () => {
      try {
        const projectData = await projectService.getProjects();
        setProjects(projectData);
      } catch (err) {
        console.error("Failed to fetch filter data", err);
      }
    };
    fetchFilterData();
  }, []);

  // Fetch tasks assigned to the logged-in user
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchTasks({ search: debouncedSearchTerm, status: statusFilter, project: projectFilter, assigned_to: user?.id.toString() });
      setLoading(false);
    };

    fetchData();
  }, [debouncedSearchTerm, statusFilter, projectFilter, user]);

  const handleRowClick = (task: Task) => {
    setSelectedTask(task);
    setIsDetailsModalOpen(true);
  };

  const handleSave = () => {
    fetchTasks({ search: debouncedSearchTerm, status: statusFilter, project: projectFilter, assigned_to: user?.id.toString() });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Tasks</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 100%', '@media (min-width:900px)': { flex: '1 1 50%' } }}>
          <TextField fullWidth label="Search Tasks" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </Box>
        <Box sx={{ flex: '1 1 40%', '@media (min-width:900px)': { flex: '1 1 15%' } }}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value=""><em>All</em></MenuItem>
              <MenuItem value="TODO">To Do</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="DONE">Done</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ flex: '1 1 40%', '@media (min-width:900px)': { flex: '1 1 15%' } }}>
          <FormControl fullWidth>
            <InputLabel>Project</InputLabel>
            <Select value={projectFilter} label="Project" onChange={(e) => setProjectFilter(e.target.value)}>
              <MenuItem value=""><em>All</em></MenuItem>
              {projects.map((p: Project) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Task Title</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Due Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => (
              <TableRow 
                key={task.id} 
                hover 
                onClick={() => handleRowClick(task)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{task.title}</TableCell>
                <TableCell>{task.project_details?.name || 'N/A'}</TableCell>
                <TableCell>{task.assigned_to_details?.first_name || 'N/A'}</TableCell>
                <TableCell>
                  <Chip label={task.status.replace('_', ' ')} color={statusColors[task.status]} size="small" sx={{ textTransform: 'capitalize' }} />
                </TableCell>
                <TableCell>{task.due_date ? format(new Date(task.due_date), 'MMM dd, yyyy') : 'N/A'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <MyTaskDetailsModal open={isDetailsModalOpen} onClose={() => setIsDetailsModalOpen(false)} onSave={handleSave} task={selectedTask} />
    </Box>
  );
};

export default MyTasksPage;